import React, { useEffect, useMemo, useState } from 'react';

import { Typography, MobileStepper, Button, RadioGroup, Radio, MenuItem, TextField } from '@material-ui/core';
import { useFormik } from 'formik';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams, useHistory } from 'react-router';

import SelfTestUserInstancePrintTemplate from 'components/_print/_templates/SelfTestUserInstancePrintTemplate/SelfTestUserInstancePrintTemplate';
import PrintComponent from 'components/_print/PrintComponent/PrintComponent';
import CheckboxWithInfo from 'components/CheckboxWithInfo/CheckboxWithInfo';
import ColoredButton from 'components/ColoredButton';
import ContentArticleTile from 'components/ContentArticleTile/ContentArticleTile';
import ContentGuideTile from 'components/ContentGuideTile/ContentGuideTile';
import DialogViewWrapper from 'components/DialogViewWrapper';
import Loader from 'components/Loader';
import TypographyWithHTML from 'components/TypographyWithHTML/TypographyWithHTML';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import { AnsweredQuestion, SelfTestAssetSelectionMode, SelfTestSelectableAsset } from 'config/api/selfTests/_types';
import { selfTestApi } from 'config/api/selfTests/selfTests';
import useBoolState from 'hooks/useBoolState';
import useGetSelfTestData from 'hooks/useGetSelfTestData/useGetSelfTestData';
import useGetSelfTestProgressInstance from 'hooks/useGetSelfTestProgressInstance/useGetSelfTestProgressInstance';
import general_messages from 'messages/general_messages';
import self_test_messages from 'messages/self_test_messages';
import table_messages from 'messages/table_messages';
import WIDE_SINGLE_COLUMN_CONTENT_WIDTH from 'pages/wideSingleColumnLayout';
import PATHS from 'router/PATHS';
import useSelfTestQuestionStore from 'storages/selfTestQuestionsStorage';

import useStyles from './SingleSelfTestPage.styles';

const SELF_TEST_PROGRESS_STATUSES = {
  NEW: 'new',
  COMPLETED: 'completed',
};

const parseHtml = (value?: string | null) => {
  if (typeof value !== 'string' || !value) {
    return '';
  }

  return parse(value);
};

const SingleSelfTestPage: React.FC = () => {
  const { selfTestRootId, selfTestVersionId, selfTestProgressId } = useParams() as unknown as {
    selfTestRootId: string;
    selfTestVersionId: string;
    selfTestProgressId: number;
  };
  const styles = useStyles();
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const { state: testIsFinished, setTrue: setTestIsFinished } = useBoolState(false);
  const [newInstanceId, setNewInstanceId] = useState<number | null>(null);
  const [isNewInstance, setIsNewInstance] = useState<boolean | null>(null);
  const [resultIsLoaded, setResultIsLoaded] = useState(false);
  const [isRoot, setIsRoot] = useState<boolean | null>(true);
  const [progressMetadata, setProgressMetadata] = useState<{
    title: string;
    assetId: number | string | '';
    assetChanged: boolean;
  }>({
    title: '',
    assetId: '',
    assetChanged: false,
  });
  const history = useHistory();
  const { questionInstances, setQuestion, resetQuestions, updateQuestion } = useSelfTestQuestionStore();

  useEffect(() => {
    resetQuestions();
  }, []);

  useEffect(() => {
    setIsNewInstance(!selfTestProgressId);
  }, [selfTestProgressId]);

  const progressId = selfTestProgressId || newInstanceId;
  const progressData = useGetSelfTestProgressInstance(progressId, isNewInstance);
  const { data, isLoading, questions, maxSteps } = useGetSelfTestData(selfTestVersionId);
  const assetSelectionMode = data?.selfTestData?.assetSelectionMode || SelfTestAssetSelectionMode.NONE;
  const assetSelectionEnabled = assetSelectionMode !== SelfTestAssetSelectionMode.NONE;
  const selectableAssets = useQuery<SelfTestSelectableAsset[]>(
    [QUERY_KEYS.GET_SELF_TEST_SELECTABLE_ASSETS, selfTestVersionId],
    () => selfTestApi.getSelectableAssets(selfTestVersionId),
    {
      enabled: Boolean(data?.selfTestData && assetSelectionEnabled),
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const createSelfTestInstance = useMutation(QUERY_KEYS.CREATE_SELF_TEST_PROGRESS, selfTestApi.createSelfTestInstance, {
    onSuccess: ({ id }) => {
      setNewInstanceId(id);
      setIsNewInstance(false);
      setIsRoot(false);
      const nextLocation = {
        pathname: `${PATHS.SELF_TESTS}/${selfTestRootId}/${selfTestVersionId}/${id}`,
      };
      if (typeof history.replace === 'function') history.replace(nextLocation);
      else history.push(nextLocation);
    },
  });
  const updateMetadata = useMutation(selfTestApi.updateSelfTestMetadata, {
    onSuccess: updatedMetadata => {
      setProgressMetadata({
        title: updatedMetadata?.title || '',
        assetId: updatedMetadata?.asset?.id || '',
        assetChanged: false,
      });
    },
  });

  const handleNextStep = () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    formik.resetForm();
    if (maxSteps) {
      if (activeStep === maxSteps - 1) {
        setTestIsFinished();
      }
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    }
  };

  const createQuestionInstance = useMutation(QUERY_KEYS.ANSWER_QUESTION, selfTestApi.createQuestionInstance, {
    onSuccess: questionData => {
      handleNextStep();
      setQuestion(questionData);
    },
  });

  const editQuestionInstance = useMutation(QUERY_KEYS.ANSWER_QUESTION, selfTestApi.editQuestionInstance, {
    onSuccess: questionData => {
      handleNextStep();
      updateQuestion(questionData);
    },
  });

  // query for the end of the test
  const testResult = useQuery(
    [QUERY_KEYS.GET_SELF_TEST_RESULT, newInstanceId, progressData.currentTestVersionId],
    () => selfTestApi.getTestResult(newInstanceId, progressData.currentTestVersionId),
    {
      enabled: testIsFinished,
      onSuccess: () => {
        setIsRoot(false);
        setResultIsLoaded(true);
      },
    },
  );
  const testProgress = useQuery(
    [QUERY_KEYS.GET_SELF_TEST_RESULT_PROGRESS_DATA, newInstanceId, progressData.currentTestVersionId],
    () => selfTestApi.getProgressDataAfterFinishingTest(newInstanceId, progressData.currentTestVersionId),
    {
      enabled: testIsFinished,
      onSuccess: ({ id }) => {
        history.push({
          pathname: `${PATHS.SELF_TESTS}/${selfTestRootId}/${selfTestVersionId}/${id}`,
        });
        setIsRoot(false);
        setResultIsLoaded(true);
      },
    },
  );

  useEffect(() => {
    if (progressData.data) {
      setProgressMetadata({
        title: progressData.data.title || '',
        assetId: progressData.data.asset?.id || '',
        assetChanged: false,
      });
    }
  }, [progressData.data?.id]);

  useEffect(() => {
    setProgressMetadata(current => ({
      ...current,
      title: '',
      assetId: '',
      assetChanged: false,
    }));
  }, [selfTestVersionId]);

  useEffect(() => {
    if (!isNewInstance || !data?.selfTestData) return;
    setProgressMetadata(current => ({
      ...current,
      title: data?.selfTestData?.heading || '',
    }));
  }, [data?.selfTestData?.heading, data?.selfTestData?.versionId, isNewInstance]);

  useEffect(() => {
    if (progressData.data?.progressStatus === SELF_TEST_PROGRESS_STATUSES.COMPLETED) {
      setTestIsFinished();
      setIsRoot(false);
    }
  }, [progressData]);

  const startSelfTest = () => {
    if (progressId) {
      setIsRoot(false);
      return;
    }
    const payload: { selfTestId: string; title: string; assetId?: number | string | null } = {
      selfTestId: selfTestVersionId,
      title: progressMetadata.title,
    };
    if (assetSelectionEnabled) payload.assetId = progressMetadata.assetId || null;
    createSelfTestInstance.mutate(payload);
  };

  const saveProgressMetadata = async () => {
    if (!progressId || progressData.data?.progressStatus === SELF_TEST_PROGRESS_STATUSES.COMPLETED) return;
    const payload: { id: number | string; title: string; assetId?: number | string | null } = {
      id: progressId,
      title: progressMetadata.title,
    };
    if (assetSelectionEnabled && progressMetadata.assetChanged) payload.assetId = progressMetadata.assetId || null;
    await updateMetadata.mutateAsync(payload);
  };

  const handleBack = async () => {
    await saveProgressMetadata();
    if (activeStep === 0) {
      setIsRoot(true);
    } else {
      setActiveStep(prevActiveStep => prevActiveStep - 1);
    }
  };

  const onSubmit = async (values: AnsweredQuestion) => {
    await saveProgressMetadata();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const checkedAnswerId = Number(values.answerId);
    const currentQuestionId = questions?.[activeStep]?.id || values.questionId;
    const dataToSend = {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      userSelfTestId: Number(values.userSelfTestId),
      questionId: Number(currentQuestionId),
      answerId: checkedAnswerId,
    };
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (formik.initialValues.answerId !== null && checkedAnswerId !== Number(formik.initialValues.answerId)) {
      const editedData = {
        ...dataToSend,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        id: values.id,
      };
      editQuestionInstance.mutate(editedData);
    } else {
      const existingQuestionInstanceAnswered = progressData?.data?.answeredQuestions?.find(
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        existingQuestion => Number(currentQuestionId) === existingQuestion.questionId,
      );
      const existingQuestionInstance = questionInstances?.find(
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        existingQuestion => Number(currentQuestionId) === existingQuestion.questionId,
      );

      if (!existingQuestionInstance && !existingQuestionInstanceAnswered) {
        createQuestionInstance.mutate(dataToSend);
      } else {
        handleNextStep();
      }
    }
  };

  const answerId =
    questions && progressData?.data?.answeredQuestions?.find(answer => answer.questionId === questions[activeStep]?.id)?.answerId;
  const questionId =
    questions && progressData?.data?.answeredQuestions?.find(answer => answer.questionId === questions[activeStep]?.id)?.questionId;
  const instanceId =
    questions && progressData?.data?.answeredQuestions?.find(answer => answer.questionId === questions[activeStep]?.id)?.id;

  const answerIdFromStorage = questions && questionInstances?.find(answer => answer.questionId === questions[activeStep]?.id)?.answerId;
  const questionIdFromStorage = questions && questionInstances?.find(answer => answer.questionId === questions[activeStep]?.id)?.questionId;
  const instanceIdFromStorage = questions && questionInstances?.find(answer => answer.questionId === questions[activeStep]?.id)?.id;

  const formik = useFormik({
    initialValues: {
      userSelfTestId: newInstanceId || progressData.currentTestVersionId,
      answerId: answerId?.toString() || answerIdFromStorage?.toString() || null,
      questionId: questionId || questionIdFromStorage || null,
      id: instanceId || instanceIdFromStorage || null,
    },
    onSubmit,
    enableReinitialize: true,
  });

  const onChange = (event: { target: HTMLInputElement }) => {
    formik.setFieldValue('answerId', event.target.value);
  };

  useEffect(() => {
    if (questions?.length && !testIsFinished) {
      formik.setFieldValue('questionId', questions[activeStep].id);
    }
  }, [activeStep, questions, testIsFinished]);

  const metadataReadOnly = progressData.data?.progressStatus === SELF_TEST_PROGRESS_STATUSES.COMPLETED;
  const assetOptions = useMemo(() => {
    const assets = selectableAssets.data || [];
    const selectedAssetInOptions = assets.some(asset => String(asset.id) === String(progressMetadata.assetId));
    if (!selectedAssetInOptions && progressData.data?.asset) return [progressData.data.asset, ...assets];
    return assets;
  }, [selectableAssets.data, progressData.data?.asset, progressMetadata.assetId]);
  let assetHelperText: string | undefined;
  if (selectableAssets.isLoading) assetHelperText = t(self_test_messages.progress_assets_loading);
  else if (selectableAssets.isError) assetHelperText = t(self_test_messages.progress_assets_error);
  else if (assetOptions.length === 0) assetHelperText = t(self_test_messages.progress_assets_empty);
  const completedResult = testProgress.data;

  if (isLoading) return <Loader inner />;

  return (
    <>
      <DialogViewWrapper contentSize={WIDE_SINGLE_COLUMN_CONTENT_WIDTH} title={t(self_test_messages.self_test_title)}>
        {!testIsFinished && (
          <>
            <form id='answer' onSubmit={formik.handleSubmit}>
              <div className={styles.header}>
                <Typography variant='subtitle1'>
                  <b>{data?.selfTestData?.heading}</b>
                </Typography>
                {!isRoot && (
                  <Typography variant='subtitle1'>
                    <b>{`${activeStep + 1} / ${maxSteps}`}</b>
                  </Typography>
                )}
              </div>
              {isRoot && !testIsFinished && (
                <TypographyWithHTML style={{ whiteSpace: 'pre-line' }}>{data?.selfTestData?.description || ''}</TypographyWithHTML>
              )}
              {(isRoot || progressId) && !testIsFinished && (
                <div className={styles.metadataPanel}>
                  <Typography component='h3' variant='h3'>
                    {t(self_test_messages.progress_metadata)}
                  </Typography>
                  <div className={styles.metadataFields}>
                    <TextField
                      disabled={metadataReadOnly}
                      fullWidth
                      id='self-test-progress-title'
                      label={t(self_test_messages.progress_title)}
                      onChange={event => setProgressMetadata(current => ({ ...current, title: event.target.value }))}
                      value={progressMetadata.title}
                      variant='outlined'
                    />
                    {assetSelectionEnabled && (
                      <TextField
                        disabled={metadataReadOnly || selectableAssets.isLoading || selectableAssets.isError}
                        fullWidth
                        helperText={assetHelperText}
                        id='self-test-progress-asset'
                        label={t(self_test_messages.progress_asset)}
                        onChange={event =>
                          setProgressMetadata(current => ({
                            ...current,
                            assetId: event.target.value,
                            assetChanged: true,
                          }))
                        }
                        select
                        value={progressMetadata.assetId}
                        variant='outlined'
                      >
                        <MenuItem value=''>{t(table_messages.no_data_cell)}</MenuItem>
                        {selectableAssets.isLoading && (
                          <MenuItem disabled value='__loading'>
                            {t(self_test_messages.progress_assets_loading)}
                          </MenuItem>
                        )}
                        {selectableAssets.isError && (
                          <MenuItem disabled value='__error'>
                            {t(self_test_messages.progress_assets_error)}
                          </MenuItem>
                        )}
                        {!selectableAssets.isLoading && !selectableAssets.isError && assetOptions.length === 0 && (
                          <MenuItem disabled value='__empty'>
                            {t(self_test_messages.progress_assets_empty)}
                          </MenuItem>
                        )}
                        {assetOptions.map(asset => (
                          <MenuItem key={asset.id} value={asset.id}>
                            {asset.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </div>
                </div>
              )}
              {!isRoot && !testIsFinished && (
                <>
                  <Typography variant='h3'>{questions?.length ? parseHtml(questions[activeStep].heading) : ''}</Typography>
                  <Typography className={styles.description} style={{ whiteSpace: 'pre-line' }} variant='body1'>
                    {questions?.length ? parseHtml(questions[activeStep].description) : ''}
                  </Typography>
                  <RadioGroup name='answerId' onChange={onChange} value={formik.values.answerId}>
                    {questions?.length &&
                      questions[activeStep].answers.map(answer => (
                        // @ts-ignore
                        <CheckboxWithInfo
                          key={answer.id}
                          className={styles.answer}
                          control={<Radio value={answer.id.toString()} />}
                          description={answer.description}
                          expandable
                          label={answer.heading}
                          selected={formik.values.answerId?.toString() === answer.id.toString()}
                        />
                      ))}
                  </RadioGroup>
                  <MobileStepper
                    activeStep={activeStep}
                    backButton={
                      <Button className={styles.stepperButton} color='secondary' onClick={handleBack}>
                        {t(self_test_messages.self_test_actions.previous)}
                      </Button>
                    }
                    classes={{ root: styles.stepperRoot, dots: styles.stepperDots }}
                    nextButton={
                      // @ts-ignore
                      <ColoredButton
                        className={styles.stepperButton}
                        color='secondary'
                        customColor='secondary'
                        disabled={formik.values.answerId === null}
                        type='submit'
                      >
                        {maxSteps && activeStep === maxSteps - 1
                          ? t(self_test_messages.self_test_actions.finish_test)
                          : t(self_test_messages.self_test_actions.next)}
                      </ColoredButton>
                    }
                    position='static'
                    steps={maxSteps || 0}
                  />
                </>
              )}
            </form>
            {isRoot && (
              <div className={styles.startTest}>
                {/* @ts-ignore */}
                <ColoredButton
                  className={styles.stepperButton}
                  color='secondary'
                  customColor='secondary'
                  disabled={createSelfTestInstance.isLoading}
                  onClick={startSelfTest}
                >
                  {t(self_test_messages.self_test_actions.next)}
                </ColoredButton>
              </div>
            )}
          </>
        )}
        {resultIsLoaded && (
          <div className={styles.resultContent} data-content-size={WIDE_SINGLE_COLUMN_CONTENT_WIDTH}>
            <div className={styles.header}>
              <Typography variant='subtitle1'>
                <b>{completedResult?.displayName || data?.selfTestData?.heading}</b>
              </Typography>
              <Typography variant='subtitle1'>
                <b>{t(self_test_messages.results)}</b>
              </Typography>
            </div>
            {completedResult && (
              <div className={styles.resultMeta}>
                <div>
                  <b>{t(self_test_messages.template_name)}: </b>
                  {completedResult.templateHeading || data?.selfTestData?.heading || '-'}
                </div>
                <div>
                  <b>{t(self_test_messages.progress_asset)}: </b>
                  {completedResult.assetName || t(general_messages.no_data)}
                </div>
              </div>
            )}
            <Typography variant='h2'>
              {t(self_test_messages.score_message, {
                currentScore: completedResult?.currentScore,
                maxScore: completedResult?.maxScore,
              })}
            </Typography>
            <Typography variant='h3'>{completedResult?.feedback?.heading || testResult.data?.heading}</Typography>
            <Typography variant='body1'>{completedResult?.feedback?.description || testResult.data?.description}</Typography>
            {completedResult && (
              <PrintComponent
                documentTitle={completedResult.displayName || completedResult.templateHeading || 'Self Test'}
                templateComponent={<SelfTestUserInstancePrintTemplate result={completedResult} />}
              />
            )}
            <Typography className={styles.description} variant='h3'>
              {t(self_test_messages.recommendations)}
            </Typography>
            {completedResult?.recommendationTexts.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className={styles.accordionTile} style={{ whiteSpace: 'pre-line' }}>
                {parseHtml(item.text)}
              </div>
            ))}
            <div className={styles.recommendedGuides}>
              {completedResult?.recommendedGuides.map(guide => (
                <ContentGuideTile key={guide.id} data={guide} minimal />
              ))}
            </div>
            <div className={styles.recommendedGrid}>
              {completedResult?.recommendedArticles.map(article => (
                <ContentArticleTile key={article.id} data={article} smallTile withImage />
              ))}
            </div>
            {completedResult?.questions?.map(question => (
              <div key={question.questionId} className={styles.resultQuestion}>
                <Typography variant='h3'>{parseHtml(question.heading)}</Typography>
                <Typography variant='body1'>{question.selectedAnswer?.heading || t(self_test_messages.no_answer)}</Typography>
              </div>
            ))}
          </div>
        )}
      </DialogViewWrapper>
    </>
  );
};

export default SingleSelfTestPage;
